import React from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useNavigate } from "react-router-dom";
import { searchSettings } from "../../services/provider_search";
import { trackEvent } from "../../services/analytics_v2";

export default function SelectPlace() {
  const navigate = useNavigate();
  const {
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading,
    placesService,
  } = usePlacesService(searchSettings);

  const [searchTerm, setSearchTerm] = React.useState<string | null>(null);

  const handleClickPlace = (placeId: string) => {
    if (!placesService) {
      throw new Error("Expected places service to be defined");
    }

    placesService.getDetails(
      {
        placeId,
      },
      (placeDetails) => {
        if (placeDetails) {
          trackEvent("v2ps-select_location-clicked_location", {
            address: placeDetails.formatted_address,
          });
        }

        navigate(
          `/provider_search/place?&lat=${placeDetails?.geometry?.location?.lat()}&lng=${placeDetails?.geometry?.location?.lng()}&name=${
            placeDetails?.formatted_address
          }`
        );
      }
    );
  };

  const handleClickUseLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        trackEvent("v2ps-select_location-use_current_location");

        navigate(
          `/provider_search/place?&lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&name=Current%20location`
        );
      },
      () => {
        console.warn("Location request was cancelled");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
      }
    );
  };

  React.useEffect(() => {
    if (!searchTerm) {
      return;
    }

    getPlacePredictions({ input: searchTerm });
  }, [searchTerm]);

  // Capture search terms used. This is wrapped in a set timeout to ensure
  // that we only capture "full" search terms used. Without this, we would
  // capture each letter as it was entered.
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (!searchTerm || isPlacePredictionsLoading === true) {
        return;
      }

      trackEvent("v2ps-select_location-use_current_location", {
        term: searchTerm,
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchTerm, isPlacePredictionsLoading]);

  const defaultStatuMessage = "Enter a postcode, suburb or address";

  const statusMessage = (() => {
    if (!searchTerm) {
      return defaultStatuMessage;
    }

    if (isPlacePredictionsLoading === true) {
      return `Searching for ${searchTerm}`;
    }

    if (placePredictions.length === 0) {
      return `Unable to find any locations called ${searchTerm}`;
    }

    if (placePredictions.length > 0) {
      return `Found ${placePredictions.length} results`;
    }
  })();

  const renderedResults = placePredictions.map((placePrediction) => {
    const placeId = placePrediction.place_id;

    return (
      <button
        key={placeId}
        className="block p-3 border-b border-gray-200 text-left hover:bg-gray-50 w-full"
        onClick={() => handleClickPlace(placeId)}
      >
        {placePrediction.description}
      </button>
    );
  });

  return (
    <div className="px-8 py-8 md:py-32 text-center max-w-2xl mx-auto">
      <h1 className="text-4xl">Find an optometrist near you</h1>
      <label className="text-lg mb-2 mt-8 block" htmlFor="search_term">
        Enter a postcode, suburb or address
      </label>

      <input
        id="search_term"
        className="py-2 px-4 border-2 rounded border-slate-500 block w-full"
        placeholder={statusMessage}
        onChange={(evt) => {
          setSearchTerm(evt.target.value);
        }}
      />

      {renderedResults.length > 0 && (
        <div className="shadow-md rounded bg-white mt-2 mb-2">
          {renderedResults}
        </div>
      )}

      {statusMessage && statusMessage !== defaultStatuMessage && (
        <p role="status" className="italic block mt-2 mb-2 text-center">
          {statusMessage}
        </p>
      )}

      <div className="flex items-center justify-center my-4 font-bold">OR</div>

      <div className="flex items-center justify-center">
        <button className="btn btn-primary" onClick={handleClickUseLocation}>
          Use my current location
        </button>
      </div>

      <div className="mt-8 max-w-prose text-left">
        <p className="my-4">
          Our Find a Provider page is powered by{" "}
          <a
            target="_blank"
            href="https://widget.nhsd.healthdirect.org.au/v1/widget/search?widgetId=9b5494f2-b4e6-495b-8d9c-e813dcebb7ca&amp;types=%5Bservices_types%5D%3Aoptometry+service"
          >
            Healthdirect's National Health Services Directory (NHSD)
          </a>
          .
        </p>
        <h2 className="my-4">Incorrect details in search results</h2>
        <p className="my-4">
          If you experience any problems using the National Health Services
          Directory, or if you have a general query or would like to give
          feedback,{" "}
          <a href="mailto:nhsd@healthdirect.org.au">
            please email the NHSD team
          </a>{" "}
          with the details of your query along with your name and contact
          details.
        </p>
        <h2 className="my-4">Register your Services</h2>
        <p className="my-4">
          If you are a provider and don't see your information here then please
          first check if it is an{" "}
          <a
            href="https://widget.nhsd.healthdirect.org.au/v1/widget/search?widgetId=9b5494f2-b4e6-495b-8d9c-e813dcebb7ca&amp;types=%5Bservices_types%5D%3Aoptometry+service"
            target="_blank"
          >
            existing listing in the NHSD
          </a>
        </p>
        <p className="my-4">
          If your service is not already listed,{" "}
          <a
            href="https://healthdirect-serviceline.atlassian.net/servicedesk/customer/portal/3/create/13"
            target="_blank"
          >
            complete the NHSD online registration form
          </a>{" "}
          so it can be found on the KeepSight website.
        </p>
      </div>
    </div>
  );
}
